import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Save, ChevronDown, ChevronUp } from 'lucide-react';
import apiService from '../services/api';
import './ETPOS/CommonStyles.css';
import './ETPOS/AddSellStyles.css';
import './CardForm.css';

const AddRoleNew = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const editRole = location.state?.role;
    const [loading, setLoading] = useState(false);
    const [permissionGroups, setPermissionGroups] = useState({});
    const [businessId, setBusinessId] = useState(null);
    const [formData, setFormData] = useState({
        name: editRole?.name || '',
        permissions: editRole?.permissions?.map(p => p.id) || [],
        is_default: editRole?.is_default || false
    });
    const [expandedGroups, setExpandedGroups] = useState({});

    useEffect(() => {
        loadPermissionGroups();
        loadBusinessId();
    }, []);

    const loadBusinessId = async () => {
        try {
            const userDetails = await apiService.getUserDetails();
            let bid = userDetails?.business_id;

            // Fallback to default business_id if not found
            if (!bid) {
                console.warn('Business ID not found in user details, using default: 40');
                bid = 40;
            }

            setBusinessId(bid);
        } catch (error) {
            console.error('Error loading business ID:', error);
            // Set default business_id on error
            setBusinessId(40);
        }
    };

    const loadPermissionGroups = async () => {
        try {
            setLoading(true);
            const userDetails = await apiService.getUserDetails();
            let bid = userDetails?.business_id;

            if (!bid) {
                bid = 40; // Default business_id
            }

            const response = await apiService.getPermissionGroups(bid);
            if (response.success && response.data) {
                setPermissionGroups(response.data);

                const initialExpanded = {};
                Object.keys(response.data).forEach((key, index) => {
                    initialExpanded[key] = index === 0;
                });
                setExpandedGroups(initialExpanded);
            }
        } catch (error) {
            console.error('Error loading permission groups:', error);
            alert('Failed to load permissions. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const togglePermission = (permissionId) => {
        setFormData(prev => {
            const newPermissions = prev.permissions.includes(permissionId)
                ? prev.permissions.filter(p => p !== permissionId)
                : [...prev.permissions, permissionId];
            return { ...prev, permissions: newPermissions };
        });
    };

    const toggleGroup = (groupKey) => {
        setExpandedGroups(prev => ({
            ...prev,
            [groupKey]: !prev[groupKey]
        }));
    };

    const toggleAllGroups = (expand) => {
        const newExpandedState = {};
        Object.keys(permissionGroups).forEach(key => {
            newExpandedState[key] = expand;
        });
        setExpandedGroups(newExpandedState);
    };

    const toggleGroupPermissions = (groupKey) => {
        const group = permissionGroups[groupKey];
        if (!group || !group.permissions) return;

        const groupPermissionIds = group.permissions.map(p => p.id);
        const allSelected = groupPermissionIds.every(id => formData.permissions.includes(id));

        setFormData(prev => {
            let newPermissions = [...prev.permissions];
            if (allSelected) {
                newPermissions = newPermissions.filter(id => !groupPermissionIds.includes(id));
            } else {
                groupPermissionIds.forEach(id => {
                    if (!newPermissions.includes(id)) {
                        newPermissions.push(id);
                    }
                });
            }
            return { ...prev, permissions: newPermissions };
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!businessId) {
            alert('Business ID not found. Please try again.');
            return;
        }

        if (formData.permissions.length === 0) {
            alert('Please select at least one permission.');
            return;
        }

        try {
            setLoading(true);
            const roleData = {
                business_id: businessId,
                name: formData.name,
                permissions: formData.permissions,
                is_default: formData.is_default
            };

            let response;
            if (editRole) {
                response = await apiService.updateRole(editRole.id, roleData);
            } else {
                response = await apiService.createRole(roleData);
            }

            if (response.success) {
                alert(editRole ? 'Role updated successfully!' : 'Role created successfully!');
                navigate('/users', { state: { activeTab: 'roles' } });
            } else {
                alert(response.message || 'Failed to save role');
            }
        } catch (error) {
            console.error('Error saving role:', error);
            alert(error.message || 'Failed to save role. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    if (loading && Object.keys(permissionGroups).length === 0) {
        return (
            <div className="card-form-container">
                <div style={{ padding: '2rem', textAlign: 'center' }}>
                    <p>Loading permissions...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="card-form-container">
            <div className="page-header" style={{ marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '1rem' }}>
                <button
                    onClick={() => navigate('/users', { state: { activeTab: 'roles' } })}
                    style={{
                        background: 'white',
                        border: '1px solid #e2e8f0',
                        borderRadius: '8px',
                        padding: '0.5rem 0.75rem',
                        cursor: 'pointer',
                        fontSize: '1.2rem',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minWidth: '40px',
                        height: '40px'
                    }}
                    title="Back"
                >
                    ←
                </button>
                <h3 style={{ margin: 0, fontSize: '1.25rem', color: '#1e293b' }}>
                    {editRole ? 'Edit Role' : 'Add Role'}
                </h3>
            </div>

            <form id="roleForm" onSubmit={handleSubmit} className="sell-form" style={{ gap: '0.5rem' }}>
                <div className="form-section" style={{ padding: '0.75rem 1.5rem', marginBottom: '0' }}>
                    <div className="form-row" style={{ gap: '1rem', marginBottom: '0.75rem' }}>
                        <div className="form-group">
                            <label>Role Name *</label>
                            <input
                                type="text"
                                className="form-input"
                                name="name"
                                value={formData.name}
                                onChange={handleChange}
                                required
                                disabled={editRole?.is_default}
                            />
                            {editRole?.is_default && (
                                <small style={{ color: '#64748b', fontSize: '0.85rem' }}>
                                    Default roles cannot be renamed
                                </small>
                            )}
                        </div>
                    </div>
                </div>

                <div className="form-section" style={{ padding: '1.5rem', marginBottom: '0' }}>
                    <div className="permissions-top-header">
                        <h3>Permissions</h3>
                        <div className="header-buttons">
                            <button
                                type="button"
                                onClick={() => toggleAllGroups(true)}
                                className="btn btn-sm btn-outline-primary"
                                style={{ fontSize: '0.85rem' }}
                            >
                                Expand All
                            </button>
                            <button
                                type="button"
                                onClick={() => toggleAllGroups(false)}
                                className="btn btn-sm btn-outline-secondary"
                                style={{ fontSize: '0.85rem' }}
                            >
                                Collapse All
                            </button>
                        </div>
                    </div>

                    {Object.entries(permissionGroups).map(([groupKey, group]) => {
                        const isExpanded = expandedGroups[groupKey];
                        const groupPermissionIds = group.permissions?.map(p => p.id) || [];
                        const isGroupAllSelected = groupPermissionIds.length > 0 &&
                            groupPermissionIds.every(id => formData.permissions.includes(id));

                        return (
                            <div key={groupKey} style={{ marginBottom: '1.5rem', border: '1px solid #e2e8f0', borderRadius: '8px', overflow: 'hidden' }}>
                                <div
                                    className="permissions-section-header"
                                    style={{ borderBottom: isExpanded ? '1px solid #e2e8f0' : 'none' }}
                                    onClick={() => toggleGroup(groupKey)}
                                >
                                    <div className="section-title">
                                        {isExpanded ? <ChevronUp size={18} color="#64748b" /> : <ChevronDown size={18} color="#64748b" />}
                                        <h4>{group.name}</h4>
                                    </div>
                                    <label
                                        className="select-all-label"
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        <input
                                            type="checkbox"
                                            checked={isGroupAllSelected}
                                            onChange={() => toggleGroupPermissions(groupKey)}
                                        />
                                        Select all
                                    </label>
                                </div>

                                {isExpanded && (
                                    <div className="permissions-section-body">
                                        <div className="permissions-grid">
                                            {group.permissions?.map((permission) => (
                                                <label key={permission.id} className="permission-item" onClick={() => togglePermission(permission.id)}>
                                                    <input
                                                        type="checkbox"
                                                        checked={formData.permissions.includes(permission.id)}
                                                        onChange={() => togglePermission(permission.id)}
                                                    />
                                                    <span>{permission.display_name}</span>
                                                </label>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>

                <div className="action-buttons-section" style={{ margin: '1rem', padding: '0 0.5rem' }}>
                    <div className="action-buttons" style={{ display: 'flex', gap: '0.75rem', justifyContent: 'center' }}>
                        <button
                            type="submit"
                            className="save-btn"
                            disabled={loading}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.5rem',
                                padding: '0.5rem 1.25rem',
                                backgroundColor: loading ? '#94a3b8' : '#3b82f6',
                                border: 'none',
                                borderRadius: '6px',
                                color: 'white',
                                fontSize: '0.9rem',
                                fontWeight: '500',
                                cursor: loading ? 'not-allowed' : 'pointer'
                            }}
                        >
                            <Save size={16} /> {loading ? 'Saving...' : (editRole ? 'Update Role' : 'Save Role')}
                        </button>
                    </div>
                </div>
            </form>
        </div>
    );
};

export default AddRoleNew;
